import { Routes, Route, Navigate } from 'react-router-dom';
import { BizIndexPage } from './features/_index';
import { LoginPage } from './features/login';
import { AuthLayout } from './features/_shared/AuthLayout';
import { AdminRouteGuard } from './features/gateway-admin/_shared/AdminRouteGuard';
import { ManagerRouteGuard } from './features/_shared/ManagerRouteGuard';
import { ServicesListPage } from './features/gateway-admin/services';
import { RoutesListPage } from './features/gateway-admin/routes';
import { ApisListPage } from './features/gateway-admin/apis';
import { TargetsListPage } from './features/gateway-admin/targets';
import { PluginsListPage } from './features/gateway-admin/plugins';
import { ConsumersListPage } from './features/gateway-admin/consumers';
import { UpstreamsListPage } from './features/gateway-admin/upstreams';
import { CertificatesListPage } from './features/gateway-admin/certificates';
import { ConsumerGroupsListPage } from './features/gateway-admin/consumer-groups';
import { RequestLogsPage } from './features/gateway-admin/request-logs';
import { LogoutConfirmPage, LogoutInspectorPage } from './features/logout';
import {
  PasswordResetRequestPage,
  PasswordResetConfirmPage,
  PasswordResetSuccessPage,
} from './features/password-reset';
import { TokenInspectorPage } from './features/token-inspector';
import { UserListPage, UserDetailPage } from './features/user-management';
import { RoleListPage, RoleDetailPage, PermissionListPage } from './features/role-management';
import { CommonCodePage } from './features/common-code';
import { AuditLogListPage, MyAuditLogPage } from './features/audit-log';
import { MyProfilePage } from './features/my-profile';
import { PermissionDemoPage } from './features/permission-demo';
import { MenuManagementPage } from './features/menu-management';
import { CommonShowcasePage, FeedbackAdminPage, AdoptionStatsPage } from './features/common-showcase';
import { ErrorDemoPage } from './features/error-demo';
import { AttachmentDemoPage } from './features/attachment-demo';
import { EventMonitorPage } from './features/event-monitor';
import { SystemSettingsPage } from './features/system-settings';
import {
  TerminologyDashboardPage,
  StandardTermListPage,
  StandardTermDetailPage,
  ForbiddenWordListPage,
  ForbiddenWordDetailPage,
  TerminologyCheckerPage,
} from './features/terminology-management';
import { DevStandardsPage } from './features/dev-standards-viewer';
import {
  ApprovalInboxPage,
  ApprovalDetailPage,
  ApprovalRequestNewPage,
  MyApprovalRequestPage,
} from './features/approval-center';
import { NotificationCenterPage } from './features/notification-center';
import { ReportListPage, ReportEditorPage, GenerationHistoryPage } from './features/report-builder';
import { SagaMonitorListPage } from './features/saga-monitor';
import { TenantSwitcherConsolePage } from './features/tenant-switcher';
import { RetentionPolicyListPage, RetentionRunHistoryPage } from './features/retention-admin';
import { ConfigCenterListPage } from './features/config-center';
import { IntegrationHubPage, FileBackendConsolePage, CallHistoryPage, TcpConsolePage, GrpcConsolePage } from './features/integration-hub';
import { SchedulerMonitorPage } from './features/scheduler-monitor';
import { ChangeHistoryViewerPage } from './features/change-history-viewer';
import { CacheAdminPage } from './features/cache-admin';
import { RealtimeConsolePage } from './features/realtime-console';
import { PlatformLabPage } from './features/platform-lab';
import { GettingStartedPage, ConsumerGuidePage } from './features/getting-started-guide';
import { ScaffoldWizardPage } from './features/project-scaffold';
import { NotFoundPage } from './features/_shared/pages/NotFoundPage';

export function AppRouter() {
  return (
    <Routes>
      {/* Public — login */}
      <Route path="/login" element={<LoginPage />} />

      {/* Public — password reset (익명 허용, token 자체가 인증 수단) */}
      <Route path="/password-reset/request" element={<PasswordResetRequestPage />} />
      <Route path="/password-reset/confirm" element={<PasswordResetConfirmPage />} />
      <Route path="/password-reset/success" element={<PasswordResetSuccessPage />} />

      {/* Protected area — AppShell 레이아웃으로 감싸기 */}
      <Route path="/" element={<Navigate to="/_index" replace />} />
      <Route
        path="/_index"
        element={
          <AuthLayout>
            <BizIndexPage />
          </AuthLayout>
        }
      />

      {/* Gateway Admin — ROLE_ADMIN required, AppShell 포함 */}
      {/* R14-#370: /gateway/overview → /gateway/routes 301 호환 redirect */}
      <Route
        path="/gateway/overview"
        element={<Navigate to="/gateway/routes" replace />}
      />
      <Route
        path="/gateway/services"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <ServicesListPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/gateway/routes"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <RoutesListPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/gateway/apis"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <ApisListPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/gateway/targets"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <TargetsListPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/gateway/plugins"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <PluginsListPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/gateway/consumers"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <ConsumersListPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/gateway/upstreams"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <UpstreamsListPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/gateway/certificates"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <CertificatesListPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/gateway/consumer-groups"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <ConsumerGroupsListPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/gateway/request-logs"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <RequestLogsPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />

      {/* Logout — requires authentication */}
      <Route
        path="/logout"
        element={
          <AuthLayout>
            <LogoutConfirmPage />
          </AuthLayout>
        }
      />

      {/* Logout Inspector — ROLE_ADMIN only (개발자도구 그룹) */}
      <Route
        path="/logout/inspector"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <LogoutInspectorPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />

      {/* Token Inspector — ROLE_ADMIN only (개발자도구 그룹) */}
      <Route
        path="/dev/token-inspector"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <TokenInspectorPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />

      {/* User Management — ROLE_MANAGER 이상 (시스템 그룹) — manager 는 USER_READ 보유 (V20261001) by design (R6-011 + #246) */}
      <Route
        path="/users"
        element={
          <AuthLayout>
            <ManagerRouteGuard>
              <UserListPage />
            </ManagerRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/users/:id"
        element={
          <AuthLayout>
            <ManagerRouteGuard>
              <UserDetailPage />
            </ManagerRouteGuard>
          </AuthLayout>
        }
      />

      {/* Common Code — ROLE_MANAGER 이상 (시스템 그룹, catalog sidebarRole=ROLE_MANAGER) */}
      <Route
        path="/codes"
        element={
          <AuthLayout>
            <ManagerRouteGuard>
              <CommonCodePage />
            </ManagerRouteGuard>
          </AuthLayout>
        }
      />

      {/* My Profile — all authenticated users (sidebarGroup=null, UserDropdown entry) */}
      <Route
        path="/me"
        element={
          <AuthLayout>
            <MyProfilePage />
          </AuthLayout>
        }
      />

      {/* Permission Demo — all authenticated users (개발자도구 그룹, sidebarRole=ROLE_USER) */}
      <Route
        path="/permission-demo"
        element={
          <AuthLayout>
            <PermissionDemoPage />
          </AuthLayout>
        }
      />

      {/* Audit Log — ROLE_MANAGER 이상 (목록, catalog sidebarRole=ROLE_MANAGER), 모든 인증 사용자 (본인 기록) */}
      <Route
        path="/audit-logs"
        element={
          <AuthLayout>
            <ManagerRouteGuard>
              <AuditLogListPage />
            </ManagerRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/audit-logs/me"
        element={
          <AuthLayout>
            <MyAuditLogPage />
          </AuthLayout>
        }
      />

      {/* Role Management — ROLE_MANAGER 이상 (시스템 그룹) — manager 는 ROLE_READ 보유 (V20261001), ROLE_WRITE 미보유 → POST 403 (R7-027 + #244) */}
      <Route
        path="/roles"
        element={
          <AuthLayout>
            <ManagerRouteGuard>
              <RoleListPage />
            </ManagerRouteGuard>
          </AuthLayout>
        }
      />
      <Route
        path="/roles/:id"
        element={
          <AuthLayout>
            <ManagerRouteGuard>
              <RoleDetailPage />
            </ManagerRouteGuard>
          </AuthLayout>
        }
      />

      {/* Permission Management — ROLE_MANAGER 이상 (시스템 그룹); ROLE_WRITE 보유 시 편집 가능 (#468) */}
      <Route
        path="/permissions"
        element={
          <AuthLayout>
            <ManagerRouteGuard>
              <PermissionListPage />
            </ManagerRouteGuard>
          </AuthLayout>
        }
      />

      {/* Menu Management — ROLE_MANAGER 이상 (시스템 그룹, catalog sidebarRole=ROLE_MANAGER) */}
      <Route
        path="/menus"
        element={
          <AuthLayout>
            <ManagerRouteGuard>
              <MenuManagementPage />
            </ManagerRouteGuard>
          </AuthLayout>
        }
      />

      {/* Common Catalog — all authenticated users (홈 그룹, sidebarRole=ROLE_USER, sidebarOrder=15) */}
      <Route
        path="/showcase"
        element={
          <AuthLayout>
            <CommonShowcasePage />
          </AuthLayout>
        }
      />

      {/* Catalog Feedback Admin — ROLE_ADMIN only (queue #663 §UAT-2026-07-09-R663 — 카탈로그 피드백/문의 관리) */}
      <Route
        path="/showcase/feedback"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <FeedbackAdminPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />

      {/* Catalog Adoption Stats — ROLE_ADMIN only (queue #687 §UAT-2026-07-14-R687 — 전 모듈 채택 현황 관리) */}
      <Route
        path="/showcase/adoption"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <AdoptionStatsPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />

      {/* Error Demo — all authenticated users (개발자도구 그룹, sidebarRole=ROLE_USER, sidebarOrder=50) */}
      <Route
        path="/error-demo"
        element={
          <AuthLayout>
            <ErrorDemoPage />
          </AuthLayout>
        }
      />

      {/* Attachment Demo — all authenticated users (개발자도구 그룹, sidebarRole=ROLE_USER, sidebarOrder=60) */}
      <Route
        path="/attachments"
        element={
          <AuthLayout>
            <AttachmentDemoPage />
          </AuthLayout>
        }
      />

      {/* Event Monitor — ROLE_ADMIN only (개발자도구 그룹, sidebarOrder=70) */}
      <Route
        path="/event-monitor"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <EventMonitorPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />

      {/* System Settings — ROLE_ADMIN only (시스템 그룹, sidebarOrder=70) */}
      <Route
        path="/system-settings"
        element={
          <AuthLayout>
            <AdminRouteGuard>
              <SystemSettingsPage />
            </AdminRouteGuard>
          </AuthLayout>
        }
      />

      {/* Terminology Management — ROLE_USER+ 조회, ROLE_ADMIN 편집 (시스템 그룹, sidebarOrder=25) */}
      {/* 조회는 모든 인증 사용자 허용 — 편집 버튼/Dialog 는 페이지 내부에서 role 체크 */}
      <Route
        path="/admin/terminology"
        element={
          <AuthLayout>
            <TerminologyDashboardPage />
          </AuthLayout>
        }
      />
      <Route
        path="/admin/terminology/standard-terms"
        element={
          <AuthLayout>
            <StandardTermListPage />
          </AuthLayout>
        }
      />
      <Route
        path="/admin/terminology/standard-terms/:id"
        element={
          <AuthLayout>
            <StandardTermDetailPage />
          </AuthLayout>
        }
      />
      <Route
        path="/admin/terminology/forbidden-words"
        element={
          <AuthLayout>
            <ForbiddenWordListPage />
          </AuthLayout>
        }
      />
      <Route
        path="/admin/terminology/forbidden-words/:id"
        element={
          <AuthLayout>
            <ForbiddenWordDetailPage />
          </AuthLayout>
        }
      />
      {/* enhance-biz #661 — 온라인 검사기. authenticated() 만 (ROLE_GUEST 포함), 편집 권한 불요 */}
      <Route
        path="/admin/terminology/checker"
        element={
          <AuthLayout>
            <TerminologyCheckerPage />
          </AuthLayout>
        }
      />

      {/* Dev Standards Viewer — 모든 인증 사용자 조회, 업로드는 페이지 내부 ROLE_ADMIN 체크 */}
      <Route
        path="/standards"
        element={
          <AuthLayout>
            <DevStandardsPage />
          </AuthLayout>
        }
      />

      {/* ── P2-P5 미시연 공통 시연 biz (#549-#553) ───────────────────────── */}

      {/* Approval Center — ROLE_USER+ (업무 그룹) · 정적 경로 먼저 */}
      <Route path="/approval-center" element={<AuthLayout><ApprovalInboxPage /></AuthLayout>} />
      <Route path="/approval-center/new" element={<AuthLayout><ApprovalRequestNewPage /></AuthLayout>} />
      <Route path="/approval-center/mine" element={<AuthLayout><MyApprovalRequestPage /></AuthLayout>} />
      <Route path="/approval-center/:requestId" element={<AuthLayout><ApprovalDetailPage /></AuthLayout>} />

      {/* Notification Center — ROLE_ADMIN (시스템 그룹) */}
      <Route
        path="/notification-center"
        element={<AuthLayout><AdminRouteGuard><NotificationCenterPage /></AdminRouteGuard></AuthLayout>}
      />

      {/* Report Builder — ROLE_USER+ (업무 그룹) · 정적 경로 먼저 */}
      <Route path="/report-builder" element={<AuthLayout><ReportListPage /></AuthLayout>} />
      <Route path="/report-builder/new" element={<AuthLayout><ReportEditorPage /></AuthLayout>} />
      <Route path="/report-builder/history" element={<AuthLayout><GenerationHistoryPage /></AuthLayout>} />
      <Route path="/report-builder/:id/edit" element={<AuthLayout><ReportEditorPage /></AuthLayout>} />

      {/* Saga Monitor — ROLE_ADMIN (시스템 그룹) */}
      <Route
        path="/saga-monitor"
        element={<AuthLayout><AdminRouteGuard><SagaMonitorListPage /></AdminRouteGuard></AuthLayout>}
      />

      {/* Tenant Switcher — ROLE_ADMIN (시스템 그룹) */}
      <Route
        path="/tenant-switcher"
        element={<AuthLayout><AdminRouteGuard><TenantSwitcherConsolePage /></AdminRouteGuard></AuthLayout>}
      />

      {/* Retention Admin — ROLE_ADMIN (시스템 그룹 · sidebarOrder=46 · purge 파괴적) */}
      <Route
        path="/retention-admin"
        element={<AuthLayout><AdminRouteGuard><RetentionPolicyListPage /></AdminRouteGuard></AuthLayout>}
      />
      <Route
        path="/retention-admin/history"
        element={<AuthLayout><AdminRouteGuard><RetentionRunHistoryPage /></AdminRouteGuard></AuthLayout>}
      />

      {/* Config Center — ROLE_ADMIN (#554 · C085 중앙설정) */}
      <Route
        path="/config-center"
        element={<AuthLayout><AdminRouteGuard><ConfigCenterListPage /></AdminRouteGuard></AuthLayout>}
      />

      {/* Integration Hub — ROLE_ADMIN (#555 · C058 Feign/C059 S3/C060 SFTP) */}
      <Route
        path="/integration-hub"
        element={<AuthLayout><AdminRouteGuard><IntegrationHubPage /></AdminRouteGuard></AuthLayout>}
      />
      <Route
        path="/integration-hub/storage"
        element={<AuthLayout><AdminRouteGuard><FileBackendConsolePage /></AdminRouteGuard></AuthLayout>}
      />
      <Route
        path="/integration-hub/history"
        element={<AuthLayout><AdminRouteGuard><CallHistoryPage /></AdminRouteGuard></AuthLayout>}
      />
      {/* TCP/gRPC 콘솔 — ROLE_ADMIN (#577 · C061 TCP/C062 gRPC) */}
      <Route
        path="/integration-hub/tcp"
        element={<AuthLayout><AdminRouteGuard><TcpConsolePage /></AdminRouteGuard></AuthLayout>}
      />
      <Route
        path="/integration-hub/grpc"
        element={<AuthLayout><AdminRouteGuard><GrpcConsolePage /></AdminRouteGuard></AuthLayout>}
      />

      {/* Scheduler Monitor — ROLE_ADMIN (#556 · C064/C065 스케줄+C022) */}
      <Route
        path="/scheduler-monitor"
        element={<AuthLayout><AdminRouteGuard><SchedulerMonitorPage /></AdminRouteGuard></AuthLayout>}
      />

      {/* Change History Viewer — ROLE_ADMIN/ROLE_EXEC (#558 · C047/C048/C045 감사이력) */}
      <Route
        path="/change-history-viewer"
        element={<AuthLayout><AdminRouteGuard><ChangeHistoryViewerPage /></AdminRouteGuard></AuthLayout>}
      />

      {/* Cache Admin — ROLE_ADMIN (#559 · C051/C050/C026 캐시) */}
      <Route
        path="/cache-admin"
        element={<AuthLayout><AdminRouteGuard><CacheAdminPage /></AdminRouteGuard></AuthLayout>}
      />

      {/* Realtime Console — ROLE_USER+ (#560 · C063 WebSocket) */}
      <Route
        path="/realtime-console"
        element={<AuthLayout><RealtimeConsolePage /></AuthLayout>}
      />

      {/* Platform Lab — ROLE_USER+ (#561 · C025/C019/C023/C018 core) */}
      <Route
        path="/platform-lab"
        element={<AuthLayout><PlatformLabPage /></AuthLayout>}
      />

      {/* Getting Started Guide — ROLE_USER+ (#628 · 홈 그룹, 신규 BE 엔드포인트 0) */}
      <Route
        path="/getting-started"
        element={<AuthLayout><GettingStartedPage /></AuthLayout>}
      />

      {/* Consumer Guide — ROLE_USER+ (#647 · 인앱 마크다운 뷰어, GitLab 직링크 제거) */}
      <Route
        path="/getting-started/consumer-guide"
        element={<AuthLayout><ConsumerGuidePage /></AuthLayout>}
      />

      {/* Project Scaffold — ROLE_USER+ (#642 · 홈 그룹, sidebarOrder=13 · 무상태 생성기) */}
      <Route
        path="/scaffold"
        element={<AuthLayout><ScaffoldWizardPage /></AuthLayout>}
      />

      {/* Catch-all 404 — 반드시 <Routes> 말단 (구체 경로 우선 매칭 후 미매칭만 도달).
          §UAT-2026-07-08-R650 §R650-3: AuthLayout 로 감싸 AppShell(사이드바) 유지 + 미인증 시
          기존 정책대로 /login redirect 승계 (공개 경로는 이 라우트보다 앞에서 매칭됨). */}
      <Route
        path="*"
        element={
          <AuthLayout>
            <NotFoundPage />
          </AuthLayout>
        }
      />
    </Routes>
  );
}
